<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { __, getFormValuesFromLocalStorage, requestRegistrationFormFields, decodeHTMLEntities } from '@/utils';
import Modal from "@components/modal.vue";
import Hint from "@components/hint.vue";
import Plan from "./Plan.vue";
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
	ComboboxButton,
} from '@headlessui/vue'

const props = defineProps({
	number: Object,
	index: Number,
});
const emit = defineEmits(['simType', 'deleteNumber', 'editNumber', 'selectedPlan']);
const { plans } = inject('step');
const cmsPrices = ref(null);

const showDeletePopup = ref(false);
const showEsimPopup = ref(false);
const savedFields = getFormValuesFromLocalStorage();
let savedNumbersCount = ref(savedFields.phoneNumbers?.length ? savedFields.phoneNumbers.length : 0);

const telecomData = computed(() => plans.value || []);

const searchDevice = esimDevicesList.list.map(item => {
  const sanitizedDeviceName = item.device
    .toLowerCase()
    .replace(/[^a-z0-9, ]/g, '')
    .replace(/[\s,]+/g, '_');

  return {
    id: sanitizedDeviceName,
    name: item.device
  };
});

const searchQuery = ref('');

const filteredDevices = computed(() => {
	return Object.values(searchDevice).filter(device => {
		return device.name.toLowerCase().includes(searchQuery.value.toLowerCase());
	});
});

const toggleDeletePopup = () => {
	showDeletePopup.value = !showDeletePopup.value;
}

const acceptDeleting = (index) => {
	toggleDeletePopup();
	emit('deleteNumber', props.index)
}

const toggleEsimPopup = () => {
	showEsimPopup.value = !showEsimPopup.value;

}

const acceptEsim = () => {
	showEsimPopup.value = !showEsimPopup.value;
	emit('simType', { index: props.index, sim: 'esim' })
}

const generatePlanDetails = computed(() => {
    const currentPlan = plans.value?.data?.find(plan => plan.plan_id === props.number.plan);
    const price = currentPlan?.custom_fields?.app_telecom_plan_montly_prices?.[0]?.price || 0;

    let message = props.number.number;

    if (currentPlan?.title) {
        const hebrewInProgram = `\u200F${__('in the program')}\u200F`;
        const title = `\u200F${currentPlan?.title}\u200F`;

        message += ` ${hebrewInProgram} ${title} - ${price} ₪`;
    }

    return message;
});

const generateSelectedSimTypeText = computed(() => {
    let price = 0;
	const cmsPricesLoaded = cmsPrices?.value;

	if(cmsPricesLoaded && props.number?.sim_type === 'sim') {
		price = cmsPrices.value.sim_price;
	} else if(cmsPricesLoaded && props.number?.sim_type === 'esim') {
		price = cmsPrices.value.esim_price;
	}

    let message = `\u200F${__('Sim Type')}\u200F`;
	
	const type = props.number?.sim_type === 'esim' ? __('esim') : __('sim');
	
	const simType = `\u200F - ${type}\u200F`;


	message += `${simType} - ${price} ₪`;

    return message;
});

const handlePlanSelection = (plan) => {
	emit('selectedPlan', { index: props.index, plan: plan })
};

const getSavedNumbersCount = () => {
	const getFields = getFormValuesFromLocalStorage();
 	return getFields.phoneNumbers?.length ? getFields.phoneNumbers.length : 0;
}

onMounted(async () => {
	const pricesCMS = await requestRegistrationFormFields();
	cmsPrices.value = pricesCMS.data;
});

</script>
<template>
	<template v-if="number?.confirmed">
		<div class="confirmed">
			<header class="confirmed__head">
				<div class="confirmed__title">
					<h6>
						{{ __('Line') }}

						{{ (index + 1) }}:

						{{ __('Your number has been successfully selected!') }}
					</h6>
				</div><!-- /.confirmed__title -->

				<div class="confirmed__controls">
					<button
						:class="{'link-icon': true, 'hidden': number.sim_type !== 'sim'}"
						type="button"
						@click.prevent="$emit('editNumber', index)"
					>
						<span>
							<i class="ico ico--edit"></i>
						</span>

						<span>
							{{ __('Edit') }}
						</span>
					</button>

					<button class="link-icon" type="button" @click="toggleDeletePopup">
						<span>
							<i class="ico ico--delete"></i>
						</span>

						<span>
							{{ __('Delete') }}
						</span>
					</button>
				</div><!-- /.confirmed__controls -->
			</header><!-- /.confirmed__head -->

			<Modal :isActive="showDeletePopup" @close="showDeletePopup = false" :class="'popup--centered'">
				<template #head>
					<h6>
						{{ __('Pay Attention!') }}
					</h6>
				</template>

				<template #body>
					<div class="notice-delete">
						<div class="notice__body">
							<p>
								<strong>
									{{ __('You have chosen to delete this number') }}

									{{ number?.number }} <br/>
								</strong>

								<div v-if="getSavedNumbersCount() <= 1">
									{{ __('This line is the only one on your list. You have to stay with at least one line in order') }}<br />
									{{ __('Continue the process, and deleting this line will take you to the beginning of the process') }}
								</div>
								<div v-if="getSavedNumbersCount() > 1">
									{{ __('No problem at all, just make sure that you have at least one line left to continue the process.') }}
								</div>
							</p>
						</div><!-- /.notice__body -->

						<div class="notice__actions">
							<button class="notice__btn btn" type="button" @click="acceptDeleting">
								{{ __('Accept') }}
							</button>

							<button class="link-underline" type="button" @click="toggleDeletePopup">
								{{ __('Cancel') }}
							</button>
						</div><!-- /.notice__actions -->
					</div><!-- /.notice-delete -->
				</template>
			</Modal>

			<div class="confirmed__body">
				<p v-if="plans">
					{{ decodeHTMLEntities(generatePlanDetails ) }}
				</p>
				<p v-if="number?.sim_type">
					{{ generateSelectedSimTypeText }}
				</p>
				<div class="field" v-if="number.plan?.length == 0 || (typeof number.plan === 'undefined')">
					<span class="field__error">
						{{ __('No Plan is selected') }}
					</span>
					<div class="plans-container" v-if="telecomData?.data">
						<Plan
							v-for="(plan, planIndex) in telecomData.data "
							:index="planIndex"
							:plan="plan" 
							:selected="tempPlan?.plan_id === plan.plan_id"
							@numberPlan="handlePlanSelection"
						/>
					</div>
				</div>
			</div><!-- /.confirmed__body -->

			<div class="confirmed__actions">
				<h6>
					{{ __('* Select the SIM type for this number') }}
				</h6>

				<button
					class="btn-shadow"
					:class="{'is-active': number.sim_type === 'sim'}"
					type="button"
					@click="$emit('simType', { index: index, sim: 'sim' })"
					name="phoneSimType"
				>
					{{ __('I am interested in a SIM card') }}
				</button>

				<button
					class="btn-shadow"
					:class="{'is-active': number.sim_type === 'xphone'}"
					type="button"
					@click="$emit('simType', { index: index, sim: 'xphone' })"
					name="phoneSimType"
					v-if="cmsPrices?.crm_u"
				>
					{{ __('I have an XPhone SIM card') }}
				</button>

				<button
					class="btn-shadow"
					:class="{'is-active': number.sim_type === 'esim'}"
					type="button"
					name="phoneSimType"
					@click="$emit('simType', { index: index, sim: 'esim' })"
				>
					<strong>
						{{ __('I want eSIM') }}
					</strong>

					<Hint class="esim-hint">
						<h6>{{ __('Note!') }}</h6>
						<p>{{ __('Before connecting to the service, you must make sure that your device supports eSIM technology. After completing the purchase, follow the instructions that will be sent to your email') }}</p>
					</Hint>

				</button>
				<div class="field">
					<span class="field__error reduce-margin" v-if="number.sim_type?.length == 0">
						{{ __('This is a mandatory field') }}
					</span>
				</div>
				<div class="field">
					<a
					href="#"
					:class="{'link-underline__regular': true, 'hidden': number.sim_type !== 'esim'}"
					@click="toggleEsimPopup"
					>
						{{ __('does my device support ESIM') }}
					</a>
				</div>
			</div><!-- /.confirmed__actions -->
		</div><!-- /.confirm -->

		<Modal :isActive="showEsimPopup" @close="showEsimPopup = false" :class="'popup'">
			<template #head>
				<h6 class="e-sim-header">
					{{ __('Pay Attention!') }}
				</h6>

			</template>
			<template #body>
				<div class="notice-support">
					<div class="notice__body">
						<p>
							{{ __('Before connecting to the service, please make sure that your device supports eSIM technology') }}
						</p>
					</div><!-- /.notice__body -->

					<div class="notice__field">
						<div class="field">
							<label for="">
								<strong>{{ __('If your device appears here, it supports this technology') }}</strong>
							</label>

							<Combobox v-model="searchQuery">
								<div class="combobox-wrapper">
									<ComboboxInput @change="searchQuery = $event.target.value" />
									<ComboboxButton class="combobox-icon" />
								</div>
								<ComboboxOptions class="combobox-options notice__options">
									<ComboboxOption
										v-for="option in filteredDevices"
										:key="option.id"
										:value="option.name"
									>
										{{ option.name }}
									</ComboboxOption>
								</ComboboxOptions>
							</Combobox>
						</div><!-- /.field -->
					</div><!-- /.notice__field -->

					<div class="notice__foot">
						<p>
							{{ __('By clicking on "Confirm", you confirm that you have checked if your device indeed supports eSIM technology and that you can continue with the purchasing process') }}
						</p>
					</div><!-- /.notice__foot -->

					<div class="notice__actions">
						<a href="#" class="notice__btn btn" @click="acceptEsim">
							{{ __('Accept') }}
						</a><!-- /.btn -->
					</div><!-- /.notice__actions -->
				</div><!-- /.notice-support -->
			</template>
		</Modal>
	</template>
</template>
